import React, { FC, useMemo, useCallback, useState, ReactNode } from "react";
import { Stack, Box, Button, MenuItem, Select, Typography, Divider, Grid2 } from "@mui/material";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router";

import { ModalContext } from "../../../../Components/Modals";
import ModalCreateCollection, {
  ModalCreateCollectionElementProps,
} from "../../../../Components/Modals/ModalCreateCollection/modal-create-collection";
import SelectModeSubbarOpener from "../../../../Components/Banners/SelectModeBanner/opener";
import ModalAddItemsToCollection, {
  ModalAddItemsToCollectionElementProps,
} from "../../../../Components/Modals/ModalAddItemsToCollection/modal-add-items-to-collection";
import ToggleLayout from "../../../../Components/Misc/ToggleLayout";
import { SearchInput } from "../../../../Components/Misc/SearchExpand/search-expand";
import EmptyListSlot from "../../../../Components/Misc/EmptyListSlot";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import { assetService, cacheService, thumbnailService } from "../../../../Core/Api/services";
import { DbObjectCollectionInfos } from "../../../../Core/Api/Types/db-object-collection-infos";
import { routePaths } from "../../../../Router/router";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MovieCard from "../../../../Components/Cards/MovieCard/movie-card";
import { handleSelectClickObject, isItemSelectedObject } from "../../../../Utils/Helpers/selectMode";
import { GridActionsCellItemProps, GridColDef } from "@mui/x-data-grid";
import { ThumbnailSize } from "../../../../Core/Api/Thumbnail/thumbnail-api-service";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import TableCellMovie from "../../../../Components/Table/TableCellMovie/table-cell-movie";
import CollectionSingleBanner from "../../../../Components/Banners/CollectionSingleBanner/collection-single-banner";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import AssetLayerChip from "../../../../Components/Misc/Chips/AssetChip/asset-layer-chip";
import TableCellAsset from "../../../../Components/Table/TableCellAsset/table-cell-asset";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import CouldLikeCard from "../../../../Components/Cards/RecommandationCard/CouldLikeCard";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-premium";
import { produce } from "immer";
import { CollectionType } from "../../../../Core/Api/Types/db-collection-type";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import AssetCard from "../../../../Components/Cards/AssetCard/asset-card";
import AssetTypeChip from "../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import { IsDriveObject } from "../../../../Core/Utils/asset-object-utils";
import CreateAccessButton from "../../../../Components/Access/Shared/create-access-button";
import { useTranslation } from "react-i18next";
import { GroupByToMap } from "../../../../Utils/helpers";
import { useAppDispatch, useAppSelector } from "../../../../Redux/redux-store";
import { GetPanelObject, ResetPanelObject, SetPanelObject } from "../../../../Redux/Reducers/redux-slice-drive";
import { PreviewContext } from "../../Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { AssetPanel2 } from "../../../../Components/Panels/AssetPanel/asset-panel-2";

type DTOAssetTable = {
  jsx: ReactNode;
  data: {
    id: number;
    obj: DbAssetObjectInfos;
    order?: number;
  };
};

const CollectionSingle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [layout, setLayout] = useState<"grid" | "list">("grid");
  const [sort, SetSort] = useState<string>("");
  const [sortSearch, SetSortSearch] = useState<string>("");
  const [selectedObjects, setSelectedObjects] = useState<DbAssetObjectInfos[]>([]);
  const params = useParams();
  const collectionId = parseInt(params.id!);
  const [collection, SetCollection] = useState<DbObjectCollectionInfos>(new DbObjectCollectionInfos());
  const [isStorefront, setIsStorefront] = useState<boolean>(false);
  const [newOrderedObjectIds, setNewOrderedObjectIds] = useState<number[]>([]);
  const [orderChanged, setOrderChanged] = useState<boolean>(false);
  const platformMode = cacheService.GetPlatformMode();
  const panelObj = useAppSelector(GetPanelObject);

  const [loadingCollections, setLoadingCollections] = useState(true);
  const dispatch = useAppDispatch();
  const isEmpty = collection?.objectInfos?.length === 0;
  const isShared = collection?.isShared;
  const thumbSize: ThumbnailSize = ThumbnailSize.small;

  const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
    setLayout(value);
  };

  const handleRemove = (objectId: number) => {
    assetService.DeleteFromCollection(collectionId, [objectId]).then((response) => {
      if (response.success) {
        SetCollection(
          produce((draft) => {
            draft!.objectIds! = draft!.objectIds!.filter((x) => x !== objectId);
            draft!.objectInfos! = draft!.objectInfos!.filter((x) => x.id !== objectId);
          })
        );
      } else {
        enqueueCustomSnackbar(response.message ?? "Something went wrong", "error");
      }
    });
  };

  const handleUpdateOrder = (objectId: number, direction: "Up" | "Down") => {
    let newObjectIds = collection?.objectIds;

    let index = newObjectIds?.findIndex((x) => x === objectId);
    if (direction === "Up") {
      [newObjectIds![index!], newObjectIds![index! + 1]] = [newObjectIds![index! + 1], newObjectIds![index!]];
    } else {
      [newObjectIds![index!], newObjectIds![index! - 1]] = [newObjectIds![index! - 1], newObjectIds![index!]];
    }
    setOrderChanged(true);
    setNewOrderedObjectIds(newObjectIds!);

    SetCollection((prevState) => ({
      ...prevState,
      objectIds: newObjectIds,
    }));
  };

  const handleOrderUpdate = () => {
    assetService
      .UpdateCollection(
        collection.id,
        collection.name!,
        collection.description || "",
        collection.type!,
        collection.location || undefined,
        collection.randomDisplay || undefined,
        collection.isPublished || undefined,
        collection.targetGroupIds || [],
        collection.targetUserIds || [],
        newOrderedObjectIds
      )
      .then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.titlesOrderUpdated"), "success");
          setOrderChanged(false);
        } else {
          enqueueCustomSnackbar(response.message ?? "Something went wrong", "error");
        }
      });
  };

  const handleCardClick = (obj: DbAssetObjectInfos) => {
    if (selectedObjects.length > 0) {
      setSelectedObjects((x) =>
        x.findIndex((a) => a.id === obj.id) !== -1 ? [...x.filter((a) => a.id !== obj.id)] : [...x, obj]
      );
      return;
    }

    dispatch(SetPanelObject(obj));
  };

  React.useEffect(() => {
    assetService.GetCollection(collectionId).then((data) => {
      SetCollection(data);
      setLoadingCollections(false);
      setIsStorefront(data.type === CollectionType.Storefront);
    });
  }, []);

  const wishlistAction: GridColDef[] = [
    {
      field: "order",
      headerName: t("common:order"),
      flex: CellSize.xxsmall,
      minWidth: CellSize.xxsmall,
      renderCell: (value: any) => <Typography> {value.row.order}</Typography>,
    },
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (value: any) => {
        if (value.row.obj.layer === ObjectLayer.Title)
          return <TableCellMovie id={value.row.obj.id} name={value.row.obj.name} />;
        return (
          <TableCellAsset
            data={{
              row: {
                title: value.row.obj.parentTitleName ?? value.row.obj.name,
                desc: value.row.obj.parentTitleName ? value.row.obj.name : "",
                type: value.row.obj.type,
                typeHide: true,
              },
            }}
          />
        );
      },
    },
    {
      field: "layer",
      headerName: t("common:layer"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (value: any) => <AssetLayerChip layer={value.row.obj.layer} />,
    },
    {
      field: "assetType",
      headerName: t("common:type"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (value: any) =>
        value.row.obj.layer === ObjectLayer.Title ? <></> : <AssetTypeChip type={value.row.obj.type} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      minWidth: CellSize.actionsSmall,
      flex: CellSize.actionsSmall,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (isStorefront) {
          if (params?.row?.order !== 1) {
            actions.push(
              <GridActionsCellItem
                disabled={isShared ?? false}
                icon={<ArrowUpwardOutlinedIcon sx={{ width: 20, height: 20 }} />}
                label={t("common:up")}
                onClick={() => handleUpdateOrder(params?.row?.id, "Up")}
              />
            );
          }
          if (params?.row?.order !== collection.objectIds?.length) {
            actions.push(
              <GridActionsCellItem
                disabled={isShared ?? false}
                icon={<ArrowDownwardOutlinedIcon sx={{ width: 20, height: 20 }} />}
                label={t("common:down")}
                onClick={() => handleUpdateOrder(params?.row?.id, "Down")}
              />
            );
          }
        }
        actions.push(
          <GridActionsCellItem
            icon={<DeleteOutlineIcon sx={{ width: 20, height: 20, color: !isShared ? "error.main" : "default" }} />}
            label={t("common:remove")}
            onClick={() => handleRemove(params?.row?.id)}
            disabled={isShared ?? false}
          />
        );

        return actions;
      },
    },
  ];

  const movieCardsData: DTOAssetTable[] = useMemo(
    () =>
      (collection?.objectInfos || []).map<DTOAssetTable>((movie, index) => {
        const productionDate = movie.metadata?.find(
          (x) => x.id === cacheService.GetMetadata().find((m) => m.tagName === "productionDate")?.id
        )?.value;

        let subtitle: string | null = null;
        if (movie.classification === ObjectClass.Drive) subtitle = "Drive";
        else {
          subtitle = `${movie.parentTitleName}`;
          movie.parentObject && (subtitle += ` / ${movie.parentObject.name}`);
        }

        return {
          jsx: (
            <Grid2 size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={`movie-grid-${index}`}>
              {movie.layer === ObjectLayer.Title && (
                <MovieCard
                  id={movie.id}
                  obj={movie}
                  selected={isItemSelectedObject(movie, selectedObjects)}
                  cover={thumbnailService.GetObjectPosterUrl(movie!.id, thumbSize)}
                  title={movie!.name ? movie!.name : ""}
                  labelList={[productionDate ? new Date(productionDate).getFullYear().toString() : "Unknown"]}
                  transparentChipText={"#" + movie!.titleCustomerId!}
                  favorite={false}
                  expand={{
                    contentList: [],
                    audioList: [],
                    subtitleList: [],
                    tagList: [],
                    quality: "",
                  }}
                  selectMode={true}
                  key={movie!.id}
                  selectedOnClick={selectedObjects.length > 0 ? true : false}
                  onSelectClick={handleSelectClickObject(selectedObjects, setSelectedObjects)}
                />
              )}
              {(movie.layer === ObjectLayer.Asset || movie.layer === ObjectLayer.Clip) && (
                <AssetCard
                  key={movie.id}
                  labelList={[movie.type]}
                  selectMode={movie.id !== 500}
                  selected={isItemSelectedObject(movie, selectedObjects)}
                  onSelectClick={handleSelectClickObject(selectedObjects, setSelectedObjects)}
                  onCardClick={() => {
                    handleCardClick(movie);
                  }}
                  active={panelObj?.id === movie.id}
                  // scale={scale}
                  infoBtn={true}
                  // onClickInfo={showAssetPanel}
                  obj={movie}
                  subtitle={subtitle}
                />
              )}
            </Grid2>
          ),
          data: {
            id: movie.id,
            obj: movie,
          },
        };
      }),
    [collection?.objectInfos, collection?.objectIds, selectedObjects, panelObj]
  );

  const listToRender = useMemo<Map<"Title" | "Asset", DTOAssetTable[]>>(() => {
    let list = movieCardsData.filter((x) => x.data.obj.name?.includesAllWords(sortSearch));
    let sortedList = collection?.objectIds
      ?.map((id) => list?.find((x) => x.data.obj.id === id))
      .reverse() as DTOAssetTable[];
    let finalList: DTOAssetTable[] = [];

    for (let index = 0; index < list.length; index++) {
      const item = sortedList[index];
      finalList.push({
        ...item,
        data: { ...item.data, order: index + 1 },
      });
    }

    if (sort === "name") finalList.sort((a, b) => a.data.obj.name?.localeCompare(b.data.obj.name));
    if (sort === "date") finalList.sort((a, b) => a.data.obj.id - b.data.obj.id); // Short cut as older ids are newer re-used

    let groupedByList = GroupByToMap(finalList, (x) => (x.data.obj.layer === ObjectLayer.Title ? "Title" : "Asset"));

    return groupedByList;
  }, [sortSearch, sort, movieCardsData, collection]);

  const TableResume: { name: string; count: number }[] = useMemo(
    () =>
      collection?.objectInfos?.reduce<{ name: string; count: number }[]>((acc, x) => {
        let index = acc.findIndex((y) => y.name === x.layer);
        if (index === -1) {
          index = acc.length;
          acc[acc.length] = { name: x.layer, count: 0 };
        }
        acc[index].count += 1;
        return acc;
      }, []) ?? [],
    [collection?.objectInfos]
  );

  const empty = (
    <EmptyListSlot
      title={t("component:emptyListSlot.title.noTitlesInCollection")}
      icon={<BookmarksOutlinedIcon />}
      desc={t("component:emptyListSlot.desc.noTitlesAddedYet")}
      CTA={<AddItemButton collection={collection} SetCollection={SetCollection} isShared={isShared} />}
    />
  );

  return (
    <DashboardLayout
      sxContent={{ px: 2, pb: 2 }}
      Banner={
        <CollectionSingleBanner
          collectionId={collectionId}
          handleCloseClick={() => setSelectedObjects([])}
          removePostAction={() => {}}
          items={collection?.objectInfos?.filter((x) => selectedObjects.map((x) => x.id).includes(x.id)) ?? []}
        ></CollectionSingleBanner>
      }
    >
      <Stack direction="row" justifyContent="space-between" pt={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4">{collection?.name!}</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {!isShared && (
              <RenameCollectionButton
                collection={collection!}
                isStoreFront={isStorefront}
                SetCollection={SetCollection}
              />
            )}
          </Stack>
        </Stack>
        <Stack></Stack>
      </Stack>

      <Box>
        {isEmpty && !loadingCollections ? (
          empty
        ) : (
          <>
            <ListPageHeader>
              <ListPageHeaderLeft>
                <Stack direction="row" alignItems="center" gap={1}>
                  {TableResume.map((value) => {
                    return (
                      <Stack direction="row" gap={0.5}>
                        <Typography key={value.name} variant="subtitle2" fontWeight={500}>
                          {value.name + " "}
                        </Typography>
                        <Typography variant="subtitle2" component="span" color="text.tier">
                          {value.count}
                        </Typography>
                      </Stack>
                    );
                  })}
                  <Button
                    startIcon={<DoneAllIcon />}
                    variant="outlined"
                    color="inherit"
                    sx={{ ml: 2 }}
                    onClick={() =>
                      selectedObjects.length > 0
                        ? setSelectedObjects([])
                        : setSelectedObjects(collection?.objectInfos ?? [])
                    }
                  >
                    {selectedObjects.length > 0 ? t("common:unselectAll") : t("common:selectAll")}
                  </Button>
                  {layout === "list" && isStorefront && (
                    <Button disabled={!orderChanged} startIcon={<EditOutlinedIcon />} onClick={handleOrderUpdate}>
                      {t("button:updateTitleOrder")}
                    </Button>
                  )}
                </Stack>
              </ListPageHeaderLeft>
              <ListPageHeaderRight>
                <>
                  <SearchInput
                    value={sortSearch}
                    onChange={(value) => SetSortSearch(value.target.value)}
                    label={t("common:standard")}
                    placeholder={t("common:placeholder")}
                    endAdornment={<SearchIcon htmlColor="white" />}
                  />
                  <Select
                    displayEmpty
                    IconComponent={ExpandMoreRoundedIcon}
                    inputProps={{ "aria-label": "Without label" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    value={sort}
                    onChange={(value) => SetSort(value.target.value)}
                  >
                    <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                    <MenuItem value="name">{t("common:title")}</MenuItem>
                    <MenuItem value="date">{t("common:recent")}</MenuItem>
                    {TableResume.length >= 2 && <MenuItem value="date">{t("common:typeLowerCase")}</MenuItem>}
                  </Select>
                  <ToggleLayout value={layout} onChange={onLayoutChange} />
                  {!collection?.objectInfos?.some((x) => !IsDriveObject(x)) && (
                    <CreateAccessButton assets={collection.objectInfos || []} variant="outlined" />
                  )}
                  <AddItemButton collection={collection} SetCollection={SetCollection} isShared={isShared} />
                </>
              </ListPageHeaderRight>
            </ListPageHeader>

            {loadingCollections ? (
              <TableSkeleton />
            ) : layout === "grid" ? (
              listToRender.size === 0 ? (
                empty
              ) : (
                <Stack gap={3}>
                  {listToRender.get("Title") && (
                    <Stack>
                      <Typography variant="h6" mb={1.5} color="text.tier">
                        {t("common:titles")}
                      </Typography>
                      <Grid2 container columns={24} spacing={2}>
                        {listToRender.get("Title")?.map((x) => x.jsx)}
                      </Grid2>
                    </Stack>
                  )}
                  {listToRender.get("Asset") && (
                    <Stack>
                      <Typography variant="h6" mb={1.5} color="text.tier">
                        {t("common:assets")}
                      </Typography>
                      <Grid2 container columns={24} spacing={2}>
                        {listToRender.get("Asset")?.map((x) => x.jsx)}
                      </Grid2>
                    </Stack>
                  )}
                </Stack>
              )
            ) : (
              <DataTable
                rows={Array.from(listToRender.values())
                  .flat()
                  .map((x) => x.data)}
                checkboxSelection
                disableRowSelectionOnClick
                onRowClick={(data: any) => {
                  if (data?.row?.obj.layer === ObjectLayer.Title)
                    navigate(routePaths.title.path + `/${data?.row?.obj.id}`);
                  else handleCardClick(data?.row?.obj);
                }}
                columns={wishlistAction}
                initialColumnVisibility={{
                  order: isStorefront,
                }}
                selectedRows={selectedObjects.map((x) => x.id)}
                setSelectedRows={(e) =>
                  setSelectedObjects(collection?.objectInfos?.filter((x) => (e as number[]).includes(x.id)) ?? [])
                }
                noControls
              />
            )}
          </>
        )}
      </Box>
      <Divider sx={{ mt: 4, mb: 4 }} />
      <CouldLikeCard />
      <SelectModeSubbarOpener selectedObjects={selectedObjects} />
      {/* <AssetPanel
        assetId={assetPanelId}
        open={assetPanelId !== undefined}
        onClose={() => setAssetPanelId(undefined)}
        closePanel={() => setAssetPanelId(undefined)}
      /> */}
      <AssetPanel2
        onClose={() => {
          dispatch(ResetPanelObject());
        }}
        toggleEnlargePanel={() => {}}
        open={panelObj !== undefined}
        closable
        context={
          panelObj?.classification === ObjectClass.Drive
            ? PreviewContext.DriveCollectionPreview
            : PreviewContext.DistribCollectionPreview
        }
      />
    </DashboardLayout>
  );
};

export default CollectionSingle;

const RenameCollectionButton: FC<{ collection: DbObjectCollectionInfos; SetCollection; isStoreFront: boolean }> = (
  props
) => {
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleEditClick: React.MouseEventHandler = () => {
    if (!openModal) {
      return;
    }

    const onClose: ModalCreateCollectionElementProps["onClose"] = (data) => {
      props.SetCollection({ ...props.collection, name: data.name, type: data.type });
    };

    openModal<ModalCreateCollectionElementProps>(ModalCreateCollection, {
      onClose,
      data: props.collection,
      type: props.isStoreFront ? CollectionType.Storefront : CollectionType.Whishlist,
    });
  };

  return (
    <Button variant="icon" onClick={handleEditClick}>
      <EditOutlinedIcon />
    </Button>
  );
};

const AddItemButton: FC<{ collection?: DbObjectCollectionInfos; SetCollection; isShared }> = (props) => {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const addItem = useCallback(
    (asset: DbAssetObjectInfos) => {
      if (props.collection === undefined) return;
      props.collection.objectInfos = [...(props.collection.objectInfos ?? []), asset];
      props.collection.objectIds = [...(props.collection.objectIds ?? []), asset.id];
      props.SetCollection({ ...props.collection });
    },
    [props.collection, props.SetCollection]
  );

  const removeItem = useCallback(
    (asset: DbAssetObjectInfos) => {
      if (props.collection === undefined) return;
      props.collection.objectInfos = props.collection.objectInfos?.filter((x) => x.id !== asset.id) || [];
      props.collection.objectIds = props.collection.objectIds?.filter((x) => x !== asset.id) || [];
      props.SetCollection({ ...props.collection });
    },
    [props.collection, props.SetCollection]
  );

  const onAddTitle = () => {
    if (!openModal || props.collection === undefined) {
      return;
    }

    const onClose: ModalAddItemsToCollectionElementProps["onClose"] = (data) => {
      console.log(data);
    };

    openModal<ModalAddItemsToCollectionElementProps>(ModalAddItemsToCollection, {
      collectionId: props.collection.id,
      currentIds: props.collection.objectIds || [],
      onClose,
      addItem,
      removeItem,
    });
  };

  return (
    <Button startIcon={<AddIcon />} variant="contained" onClick={onAddTitle} disabled={props.isShared}>
      {t("common:addItems")}
    </Button>
  );
};
